import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useHoliday, DECO_ICONS } from "@/components/holiday/useHoliday";
import { HolidayTopDecor } from "@/components/holiday/HolidayTopDecor";
import { ClickBurst } from "@/components/holiday/ClickBurst";
import { HolidaySoundToggle } from "@/components/holiday/HolidaySoundToggle";


const HIDDEN = ["/overlay/", "/panel/"];

export const Falling = ({ theme, count = 14, testid = "holiday-deco" }) => {
  const items = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: (i * 73) % 100, size: 10 + ((i * 37) % 12), dur: 16 + ((i * 29) % 14), delay: -((i * 41) % 30), drift: ((i % 2) * 2 - 1) * (20 + (i * 13) % 40),
  })), [count]);
  const icons = DECO_ICONS[theme.deco] || DECO_ICONS.snow;
  return (
    <div className="cx-hol-fall" aria-hidden="true" data-testid={testid}>
      {items.map((p, i) => {
        const Icon = icons[i % icons.length];
        return <span key={i} className="cx-hol-flake" style={{ left: `${p.left}%`, animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`, "--drift": `${p.drift}px`, color: i % 3 ? theme.color : theme.color2 }}>
          <Icon style={{ width: p.size, height: p.size }} />
        </span>;
      })}
    </div>
  );
};

const PerkBanner = ({ perks, storeKey }) => {
  const { t, lang } = useI18n();
  const [hide, setHide] = useState(() => sessionStorage.getItem(storeKey) === "1");
  const [consent, setConsent] = useState(() => Boolean(localStorage.getItem("clipx_consent")));
  useEffect(() => {
    const on = () => setConsent(Boolean(localStorage.getItem("clipx_consent")));
    window.addEventListener("clipx-consent", on);
    return () => window.removeEventListener("clipx-consent", on);
  }, []);
  if (hide || !consent) return null;
  const p = perks[0];
  const bits = [p.promo_code ? t("hol.perk_code", { c: p.promo_code }) : null, p.bonus_pct ? t("hol.perk_bonus", { n: p.bonus_pct }) : null, p.free_pack ? t("hol.perk_pack") : null].filter(Boolean);
  return (
    <div className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-14 pointer-events-none">
    <div className="cx-hol-banner pointer-events-auto relative max-w-full flex items-center gap-2.5 rounded-full pl-4 pr-9 py-1.5 text-[11px] sm:text-[12px] text-white" style={{ "--hol": p.color, "--hol2": p.color2 }} data-testid="holiday-banner">
      <span className="font-display font-bold tracking-wide" data-testid="holiday-banner-name">{lang === "fr" ? p.fr : p.en}</span>
      <span className="opacity-90 truncate" data-testid="holiday-banner-text">{bits.join(" · ")}</span>
      <button type="button" onClick={() => { sessionStorage.setItem(storeKey, "1"); setHide(true); }} className="absolute right-2 p-1 rounded opacity-70 hover:opacity-100" aria-label="close" data-testid="holiday-banner-close"><X className="w-3.5 h-3.5" /></button>
    </div>
    </div>
  );
};

export const HolidaySkin = () => {
  const { skin, perks } = useHoliday();
  const { pathname } = useLocation();
  const hidden = HIDDEN.some((p) => pathname.startsWith(p));
  useEffect(() => {
    const el = document.documentElement;
    if (skin && !hidden) {
      el.dataset.holiday = skin.id;
      el.style.setProperty("--hol", skin.color);
      el.style.setProperty("--hol2", skin.color2);
    } else {
      delete el.dataset.holiday;
    }
  }, [skin, hidden]);
  if (hidden) return null;
  const key = `cx_hol_banner_${perks.map((p) => p.id + p.bonus_pct + p.promo_code + p.free_pack).join("_")}`;
  return (
    <>
      {perks.length > 0 && <PerkBanner key={key} storeKey={key} perks={perks} />}
      {skin && <Falling theme={skin} />}
      {skin && <HolidayTopDecor theme={skin} />}
      {skin && <ClickBurst theme={skin} />}
      {skin && <HolidaySoundToggle theme={skin} />}
    </>
  );
};

export const HolidayBadge = ({ className = "" }) => {
  const { skin } = useHoliday();
  if (!skin) return null;
  const Icon = (DECO_ICONS[skin.deco] || DECO_ICONS.snow)[0];
  return <span className={`cx-hol-badge pointer-events-none ${className}`} style={{ color: skin.color }} data-testid="holiday-logo-badge"><Icon className="w-4 h-4" /></span>;
};
